import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import SEO from "@/components/SEO";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import DemoScheduler from "@/components/DemoScheduler";
import { useBlogContactPopup } from "@/hooks/useBlogContactPopup";
import {
  ArrowRight,
  Calendar,
  Clock,
  User,
  Share2,
  Linkedin,
  Twitter,
  Facebook,
  CheckCircle2,
  KanbanSquare,
  Target,
  Lightbulb,
} from "lucide-react";

const BlogPostPlanoAcao = () => {
  const { isOpen, setIsOpen } = useBlogContactPopup(3000);

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Módulo de Plano de Ação para Formulários"
        description="Conheça o módulo de Plano de Ação da Aroan com 5W2H, Matriz GUT, Diagnóstico A3, 5 Porquês, Kanban de ações e encerramento com lições aprendidas."
        canonical="https://aroan.com.br/blog/modulo-plano-de-acao-formularios"
        ogImage="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&auto=format&fit=crop"
        author="Equipe Aroan"
        publishedTime="2026-04-10T00:00:00-03:00"
        keywords="plano de ação, 5W2H, matriz GUT, diagnóstico A3, 5 porquês, formulários, qualidade, melhoria contínua, aroan"
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", url: "https://aroan.com.br" },
          { name: "Blog", url: "https://aroan.com.br/blog" },
          { name: "Módulo de Plano de Ação para Formulários" },
        ]}
      />
      <Header />

      <section className="relative pt-32 pb-20 bg-gradient-hero overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
              Gestão de Serviços
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-8 leading-tight animate-fade-in">
              Módulo de <span className="text-primary">Plano de Ação</span> para Formulários
            </h1>

            <div className="flex flex-wrap items-center justify-center gap-6 text-muted-foreground animate-fade-in-delay-1">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                <span className="font-medium text-foreground">Equipe Aroan</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                <span>10 Abril, 2026</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                <span>8 min de leitura</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto rounded-2xl overflow-hidden shadow-2xl animate-scale-in bg-card flex items-center justify-center">
            <img
              src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&auto=format&fit=crop"
              alt="Planejamento e gestão de plano de ação"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      <section className="py-12 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

              {/* Left Column */}
              <div className="space-y-8">
                <div className="prose prose-lg dark:prose-invert max-w-none">
                  <h2 className="text-3xl font-bold mb-6 text-foreground">Introdução</h2>
                  <p className="lead text-xl text-muted-foreground leading-relaxed font-medium">
                    O <strong>Módulo de Plano de Ação</strong> da Aroan transforma respostas críticas de formulários em um fluxo estruturado de tratamento, unificando <strong>5W2H</strong>, <strong>Matriz GUT</strong>, <strong>Diagnóstico A3</strong>, <strong>5 Porquês</strong> e <strong>PDCA</strong> em uma única experiência.
                  </p>
                  <p>
                    Em vez de depender de planilhas paralelas, e-mails dispersos e acompanhamentos manuais, a operação passa a tratar não conformidades, auditorias, inspeções e ocorrências com método, rastreabilidade e execução orientada por prioridade.
                  </p>
                  <p>
                    O módulo opera em modalidade <strong>pós-envio</strong>: após a submissão de um formulário, o sistema identifica respostas críticas com base em regras configuradas e habilita a criação do plano diretamente no contexto da ocorrência.
                  </p>
                </div>

                <div className="bg-card p-6 rounded-xl border border-border">
                  <h3 className="text-2xl font-bold mb-4 text-foreground">Onde esse módulo gera mais valor</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {[
                      "Inspeções de segurança com não conformidades críticas",
                      "Auditorias de qualidade com desvios de procedimento",
                      "Checklists operacionais com falhas recorrentes",
                      "Pesquisas de satisfação com avaliações negativas",
                    ].map((item) => (
                      <div key={item} className="flex gap-3 rounded-lg border border-border/60 p-4">
                        <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                        <p className="text-sm text-muted-foreground m-0">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-card p-6 rounded-xl border border-border">
                  <div className="flex items-center gap-3 mb-4">
                    <Target className="w-6 h-6 text-primary" />
                    <h3 className="text-2xl font-bold text-foreground">1. Dados Gerais com 5W2H estratégico</h3>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    A primeira aba concentra a essência do plano: o que será feito, por que, onde, quando, como, quanto custa e quem será o responsável.
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                    <li>Título e justificativa podem vir pré-preenchidos pela resposta do formulário.</li>
                    <li>Responsáveis são filtrados automaticamente conforme a categoria selecionada.</li>
                    <li>O sistema mantém trilha de auditoria de criação, atualização e conclusão.</li>
                  </ul>
                </div>

                <div className="bg-card p-6 rounded-xl border border-border">
                  <div className="flex items-center gap-3 mb-4">
                    <Lightbulb className="w-6 h-6 text-primary" />
                    <h3 className="text-2xl font-bold text-foreground">2. Análise com Matriz GUT e Diagnóstico A3</h3>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    Antes de agir, o módulo ajuda a priorizar e entender a causa raiz. A Matriz GUT calcula o score de gravidade, urgência e tendência, enquanto o A3 organiza a investigação em seções estruturadas.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="rounded-lg border border-border/60 p-4">
                      <h4 className="font-semibold text-foreground mb-2">Matriz GUT</h4>
                      <p className="text-sm text-muted-foreground m-0">
                        Score automático de 1 a 125 com classificação entre baixa, média, alta ou crítica prioridade.
                      </p>
                    </div>
                    <div className="rounded-lg border border-border/60 p-4">
                      <h4 className="font-semibold text-foreground mb-2">Diagnóstico A3</h4>
                      <p className="text-sm text-muted-foreground m-0">
                        Situação atual, impacto, 5 Porquês, causa raiz, escopo e áreas envolvidas em uma análise padronizada.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-8">
                <div className="prose prose-lg dark:prose-invert max-w-none">
                  <h2 className="text-3xl font-bold mb-6 text-foreground">Fluxo guiado em 4 abas progressivas</h2>
                  <p>
                    A experiência foi desenhada como um modal em <strong>quatro etapas</strong>. As abas mais avançadas só ficam disponíveis após a criação inicial do plano, garantindo que a base estratégica exista antes da análise aprofundada e do desdobramento tático.
                  </p>
                </div>

                <div className="bg-card p-6 rounded-xl border border-border">
                  <div className="flex items-center gap-3 mb-4">
                    <KanbanSquare className="w-6 h-6 text-primary" />
                    <h3 className="text-2xl font-bold text-foreground">3. Ações 5W2H em formato Kanban</h3>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    O plano estratégico se desdobra em tarefas operacionais com 5W2H individual, organizadas em colunas como pendente, em andamento, bloqueada e concluída.
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                    <li>Cadastro, edição, exclusão e movimentação de cartões.</li>
                    <li>Controle de prazo, prioridade, progresso e custos por ação.</li>
                    <li>Bloqueios com motivo obrigatório e evidências anexas.</li>
                  </ul>
                </div>

                <div className="bg-card p-6 rounded-xl border border-border">
                  <div className="flex items-center gap-3 mb-4">
                    <CheckCircle2 className="w-6 h-6 text-primary" />
                    <h3 className="text-2xl font-bold text-foreground">4. Encerramento com eficácia e aprendizado</h3>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    A última etapa fecha o ciclo com verificação de eficácia e registro de lições aprendidas, fortalecendo a memória organizacional.
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                    <li>Avaliação binária da eficácia do plano, com critérios e resultados observados.</li>
                    <li>Campo condicional para ações adicionais quando o problema persiste.</li>
                    <li>Registro de lições classificadas como sucesso, melhoria ou falha.</li>
                  </ul>
                </div>

                <div className="prose prose-lg dark:prose-invert max-w-none">
                  <h2 className="text-3xl font-bold mb-6 text-foreground">Por que isso importa para operações enterprise</h2>
                  <p>
                    Em operações com múltiplas áreas, o maior problema não é apenas identificar desvios — é garantir que eles sejam tratados com consistência, prioridade correta e acompanhamento real até o encerramento.
                  </p>
                  <p>
                    Ao conectar formulários, análise, execução e aprendizado, a Aroan reduz retrabalho, melhora governança e cria um processo claro para tratar ocorrências em qualidade, segurança, manutenção, facilities, customer success e outras frentes críticas.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-primary/5 to-background border border-primary/10 rounded-2xl p-6">
                  <h3 className="text-2xl font-bold mb-4 text-foreground">Conclusão</h3>
                  <p className="text-muted-foreground mb-4">
                    O Módulo de Plano de Ação não é apenas um formulário complementar — ele é a ponte entre o apontamento de um problema e a execução estruturada da solução.
                  </p>
                  <p className="text-muted-foreground">
                    Ao reunir metodologias consolidadas em uma única experiência, a Aroan permite que empresas tratem não conformidades com mais velocidade, governança e aprendizado contínuo.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between border-y border-border py-6 mb-12 mt-12">
              <span className="font-semibold">Compartilhe este artigo:</span>
              <div className="flex gap-4">
                <Button
                  variant="ghost"
                  size="icon"
                  className="hover:text-primary hover:bg-accent"
                  onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`, "_blank")}
                >
                  <Linkedin className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="hover:text-primary hover:bg-accent"
                  onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent("Confira este artigo sobre módulo de plano de ação da Aroan!")}`, "_blank")}
                >
                  <Twitter className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="hover:text-primary hover:bg-accent"
                  onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, "_blank")}
                >
                  <Facebook className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={async () => {
                    if (navigator.share) {
                      try {
                        await navigator.share({
                          title: "Módulo de Plano de Ação para Formulários",
                          text: "Confira este artigo sobre o módulo de plano de ação da Aroan!",
                          url: window.location.href,
                        });
                      } catch (err) {
                        console.error("Error sharing:", err);
                      }
                    } else {
                      navigator.clipboard.writeText(window.location.href);
                      alert("Link copiado para a área de transferência!");
                    }
                  }}
                >
                  <Share2 className="w-5 h-5" />
                </Button>
              </div>
            </div>

            <div className="bg-foreground text-background rounded-2xl p-8 sm:p-12 text-center">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                Quer estruturar planos de ação dentro dos seus formulários?
              </h2>
              <p className="text-background/80 mb-8 max-w-2xl mx-auto">
                Veja como a Aroan conecta formulários, análise de causa raiz, execução em Kanban e aprendizado contínuo em um único fluxo operacional.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="secondary" className="group" onClick={() => window.open('/#demo', '_self')}>
                  Agende uma Demonstração
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button size="lg" variant="outline" className="bg-transparent border-background/20 text-background hover:bg-background/10" onClick={() => window.open('/materiais', '_self')}>
                  Ver Materiais
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <DemoScheduler open={isOpen} onOpenChange={setIsOpen} />
    </div>
  );
};

export default BlogPostPlanoAcao;